import { accountStore, authStore } from '@/services/auth/store';
import { changeToSession, sessionsStore } from '@customerio/hydra-session-state';

export const MASQ_KEY = 'masq_account_id';

export const getMasqId = (url: string) => {
  const u = new URL(suitableUrl(url));
  return u.searchParams.get(MASQ_KEY);
};

const suitableUrl = (url: string) => (url.startsWith('http') ? url : `http://localhost${url}`);

/**
 * Get the current masq account ID from the browser URL.
 * Returns null if not masquerading.
 */
const getCurrentMasqAccountId = (): string | null => {
  if (typeof window === 'undefined') return null;
  return getMasqId(window.location.href);
};

/**
 * Append masq_account_id to a URL if currently masquerading.
 * Only applies to account-level URLs (non-workspace routes).
 * Tries to get masq ID from URL first, then falls back to auth store.
 */
export const appendMasqParam = (url: string): string => {
  // Don't add to external URLs (mailto:, tel:, http://, https://)
  if (/^(mailto:|tel:|https?:\/\/)/.test(url)) return url;

  // Try to get masq ID from current URL first
  let masqAccountId = getCurrentMasqAccountId();

  // Fall back to auth store if not in URL
  if (masqAccountId == null) {
    const auth = authStore.get();
    const isMasq = auth?.account?.is_masq ?? false;
    if (isMasq && auth?.account?.id != null) {
      masqAccountId = String(auth.account.id);
    }
  }

  if (masqAccountId == null) return url;

  // Don't add if URL already has the param
  if (url.includes('masq_account_id=')) return url;

  // Don't add to workspace URLs (they don't need the param)
  if (url.includes('/workspaces/')) return url;

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}masq_account_id=${masqAccountId}`;
};

const CIO_SETTINGS_KEY = 'storage:cio-settings';

/**
 * Get the masquerade bar visibility setting from localStorage.
 * Returns false if not set or if an error occurs.
 */
export const getMasqBarVisibility = (): boolean => {
  try {
    const cioSettings = JSON.parse(localStorage.getItem(CIO_SETTINGS_KEY) ?? '{}');
    return cioSettings?.showAdmin ?? false;
  } catch {
    return false;
  }
};

/**
 * Set the masquerade bar visibility in localStorage.
 */
export const setMasqBarVisibility = (visible: boolean): void => {
  try {
    const cioSettings = JSON.parse(localStorage.getItem(CIO_SETTINGS_KEY) ?? '{}');
    cioSettings.showAdmin = visible;
    localStorage.setItem(CIO_SETTINGS_KEY, JSON.stringify(cioSettings));
  } catch {
    // ignore
  }
};

/**
 * Dispatch a CustomEvent to notify embedded apps of masq read-only state changes.
 * The Ember app can subscribe to this event to update its own masq service state.
 */
const MasqReadOnlyEventType = 'hydra:masq-read-only-change';

export const announceMasqReadOnlyChange = (readOnly: boolean): void => {
  window.dispatchEvent(new CustomEvent(MasqReadOnlyEventType, { detail: { readOnly } }));
};

/**
 * When masqing, we want to use the session, if multiple exist, that matches the
 * customer.io account (account 1). Can be called multiple times (e.g. load,
 * account data fetch, etc.)
 */
export const selectSessionForMasq = () => {
  const cioSession = sessionsStore.get().find((session) => session.name === 'Customer.io')?.id ?? 0;
  const urlId = getCurrentMasqAccountId();
  if (urlId != null) {
    changeToSession(cioSession);
    return;
  }

  const accountIsMasq = accountStore.get()?.is_masq ?? false;
  if (accountIsMasq) {
    changeToSession(cioSession);
    return;
  }
  return;
};
